import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { LogoUpload, PhotosUpload } from "@/components/ImageUpload";
import api, { apiError } from "@/lib/api";

export default function ProfileEditor({ role, me, onSaved }) {
  const [f, setF] = useState(me || {});
  useEffect(() => setF(me || {}), [me]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  if (!me) return null;

  const save = async () => {
    const endpoint = role === "bnb" ? "/bnb/me" : "/vendor/me";
    const payload = role === "bnb"
      ? { name: f.name, description: f.description, phone: f.phone, logo: f.logo || "", photos: f.photos || [], default_margin_pp: Number(f.default_margin_pp || 0.6) }
      : { name: f.name, phone: f.phone, hours: f.hours, closing_days: f.closing_days, logo: f.logo || "", photos: f.photos || [], offers_pickup: !!f.offers_pickup, offers_delivery: !!f.offers_delivery, delivery_cost: Number(f.delivery_cost || 0), delivery_radius_km: Number(f.delivery_radius_km || 0), delivery_min_order: Number(f.delivery_min_order || 0) };
    try { await api.put(endpoint, payload); toast.success("Profilo aggiornato"); onSaved && onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-white rounded-2xl border border-navy/10 p-5">
        <Label className="text-navy/70">Logo</Label>
        <div className="mt-3"><LogoUpload value={f.logo} onChange={(v) => set("logo", v)} /></div>
      </div>
      <div className="bg-white rounded-2xl border border-navy/10 p-5">
        <Label className="text-navy/70">Fotografie</Label>
        <p className="text-xs text-navy/40 mb-3 mt-1">Mostrate agli ospiti nelle schede.</p>
        <PhotosUpload value={f.photos || []} onChange={(v) => set("photos", v)} />
      </div>
      <div className="bg-white rounded-2xl border border-navy/10 p-5 grid sm:grid-cols-2 gap-4">
        <div><Label>Nome</Label><Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} className="rounded-xl mt-1" data-testid="profile-name" /></div>
        <div><Label>Telefono</Label><Input value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} className="rounded-xl mt-1" data-testid="profile-phone" /></div>
        {role === "bnb" && (
          <>
            <div className="sm:col-span-2"><Label>Descrizione</Label><Textarea value={f.description || ""} onChange={(e) => set("description", e.target.value)} className="rounded-xl mt-1" rows={3} data-testid="profile-desc" /></div>
            <div><Label>Margine predefinito € /pers.</Label><Input type="number" step="0.01" value={f.default_margin_pp ?? 0.6} onChange={(e) => set("default_margin_pp", e.target.value)} className="rounded-xl mt-1" data-testid="profile-margin" /></div>
          </>
        )}
        {role === "vendor" && (
          <>
            <div><Label>Orari</Label><Input value={f.hours || ""} onChange={(e) => set("hours", e.target.value)} className="rounded-xl mt-1" data-testid="profile-hours" /></div>
            <div><Label>Giorni di chiusura</Label><Input value={f.closing_days || ""} onChange={(e) => set("closing_days", e.target.value)} className="rounded-xl mt-1" data-testid="profile-closing" /></div>
            <div><Label>Costo consegna €</Label><Input type="number" step="0.01" value={f.delivery_cost ?? 0} onChange={(e) => set("delivery_cost", e.target.value)} className="rounded-xl mt-1" data-testid="profile-delivery" /></div>
            <div><Label>Raggio consegna km</Label><Input type="number" value={f.delivery_radius_km ?? 0} onChange={(e) => set("delivery_radius_km", e.target.value)} className="rounded-xl mt-1" data-testid="profile-radius" /></div>
            <label className="flex items-center justify-between sm:col-span-2 bg-cream rounded-xl p-3"><span className="text-sm text-navy/70">Offro ritiro</span><Switch checked={!!f.offers_pickup} onCheckedChange={(v) => set("offers_pickup", v)} /></label>
            <label className="flex items-center justify-between sm:col-span-2 bg-cream rounded-xl p-3"><span className="text-sm text-navy/70">Offro consegna</span><Switch checked={!!f.offers_delivery} onCheckedChange={(v) => set("offers_delivery", v)} /></label>
          </>
        )}
      </div>
      <Button onClick={save} className="rounded-full bg-navy text-white h-11 px-6" data-testid="profile-save"><Save className="w-4 h-4 mr-2" />Salva profilo</Button>
    </div>
  );
}
